<template>
  <div
    v-tippy="{
      theme: 'WSM',
      content: notEnoughTokens && !isTargetShot ? $te('notEnoughTokens') : null,
      placement: 'top',
      textAlign: 'center',
    }"
    :data-event-theme="namespace"
    class="target-item flexing"
    :class="{
      'target-item--done': targetData.isShot,
      'target-item--best': targetData.isShot && targetData.isBest,
    }"
    @click="$debounce(handleClick)"
  >
    <div v-if="targetData.isShot" class="target-item__content">
      <app-main-icon :icon-size="56" :icon-name="targetData.targetIcon" class="inline-block" />
      <div class="target-item__content-value flexing text-texts-standard-default text-30 font-bold">
        {{ $filters.$formatNumber(targetData.targetValue) }}
        <app-icon v-if="targetData.isShot" class="absolute box-icon--done" icon-name="done-sm" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useFortuneBasketStore } from '@/store/pinia/events/fortuneBasketStore'
import { useEventInfoStore } from '@/store/pinia/events/eventInfoStore'
import { mapActions, mapState } from 'pinia'

import type { PropType } from 'vue'
import type { SpecialItem } from '@/interfaces/responses/events/fortune-basket/FortuneBasketStateApiResponse'

interface TargetItem {
  position: number
  targetIcon: string
  targetValue: number
  isShot: boolean
  isBest: boolean
}

export default defineComponent({
  name: 'TargetItem',
  props: {
    targetData: {
      type: Object as PropType<TargetItem>,
      default: null,
    },
  },
  computed: {
    ...mapState(useEventInfoStore, {
      namespace: 'getEventType',
    }),
    ...mapState(useFortuneBasketStore, {
      specialItems: 'getSpecialItems',
    }),
    getTokens(): SpecialItem {
      return this.specialItems?.find(
        (item: SpecialItem): boolean => item?.type === 'fortune_basket_token',
      )
    },
    notEnoughTokens(): boolean {
      return this.getTokens?.value === 0
    },
    isTargetShot(): boolean {
      return this.targetData.isShot
    },
  },
  methods: {
    ...mapActions(useFortuneBasketStore, ['claim']),
    handleClick(): void {
      if (this.notEnoughTokens) return
      if (this.targetData.isShot) return
      this.claim({ position: this.targetData.position })
    },
  },
})
</script>

<style lang="scss" scoped>
.target-item {
  position: relative;
  width: 9.688rem;
  height: 9.438rem;
  background: var(--fortune-basket-target-bg);
  background-size: contain;

  &:hover {
    &::before {
      content: '';
      position: absolute;
      width: 9.688rem;
      height: 9.438rem;
      background: var(--fortune-basket-target-overlay-bg);
      background-size: contain;
    }
  }

  &--done {
    background: var(--fortune-basket-target-opened-bg);
    background-size: contain;
  }

  &--best {
    background: var(--fortune-basket-target-best-bg);
    background-size: contain;
  }

  &__content {
    flex: 1 1 auto;
    z-index: 2;
    width: 3.5rem;
    height: 3.5rem;

    &-value {
      position: relative;
      margin: 0 auto;
      width: 7.25rem;
      height: 2.25rem;
      margin-bottom: -4rem;
      background-image: var(-fortune-basket-target-label-gradient);

      &::before,
      &::after {
        content: '';
        position: absolute;
        left: 0;
        width: 100%;
        height: 0.125rem;
        background-image: var(-fortune-basket-target-label-outline);
      }

      &::before {
        top: 0;
      }

      &::after {
        bottom: 0;
      }

      .box-icon--done {
        position: absolute;
        right: -1rem;
        bottom: -0.5rem;
      }
    }
  }
}
[data-event-theme='autumn'] {
  --fortune-basket-target-bg: url('#{$path-events}autumn-fair/fortune-basket/pumpkin--full.avif')
    no-repeat center;
  --fortune-basket-target-overlay-bg: url('#{$path-events}autumn-fair/fortune-basket/pumpkin--overlay.avif')
    no-repeat center;
  --fortune-basket-target-opened-bg: url('#{$path-events}autumn-fair/fortune-basket/pumpkin--opened.avif')
    no-repeat center;
  --fortune-basket-target-best-bg: url('#{$path-events}autumn-fair/fortune-basket/pumpkin--best.avif')
    no-repeat center;
  --fortune-basket-target-label-gradient: linear-gradient(
    to right,
    rgba(14, 116, 105, 0),
    rgba(14, 116, 105, 1),
    rgba(14, 116, 105, 1),
    rgba(14, 116, 105, 0)
  );
  --fortune-basket-target-label-outline: linear-gradient(
    to right,
    rgba(14, 231, 204, 0.01),
    rgba(14, 231, 204, 1),
    rgba(14, 231, 204, 0.01)
  );
}

[data-event-theme='fortune_festival'] {
  --fortune-basket-target-bg: url('#{$path-events}fortune-festival/fortune-basket/pumpkin--full.avif')
    no-repeat center;
  --fortune-basket-target-overlay-bg: url('#{$path-events}fortune-festival/fortune-basket/pumpkin--overlay.avif')
    no-repeat center;
  --fortune-basket-target-opened-bg: url('#{$path-events}fortune-festival/fortune-basket/pumpkin--opened.avif')
    no-repeat center;
  --fortune-basket-target-best-bg: url('#{$path-events}fortune-festival/fortune-basket/pumpkin--best.avif')
    no-repeat center;
  @if $isWsm {
    --fortune-basket-target-label-gradient: linear-gradient(
      to right,
      rgba(19, 132, 13, 0),
      rgba(19, 132, 13, 1),
      rgba(19, 132, 13, 1),
      rgba(19, 132, 13, 0)
    );
    --fortune-basket-target-label-outline: linear-gradient(
      to right,
      rgba(84, 220, 47, 0.01),
      rgba(84, 220, 47, 1),
      rgba(84, 220, 47, 0.01)
    );
  }
  @if $isSsm {
    --fortune-basket-target-label-gradient: linear-gradient(
      to right,
      rgba(14, 116, 105, 0),
      rgba(14, 116, 105, 1),
      rgba(14, 116, 105, 1),
      rgba(14, 116, 105, 0)
    );
    --fortune-basket-target-label-outline: linear-gradient(
      to right,
      rgba(14, 231, 204, 0.01),
      rgba(14, 231, 204, 1),
      rgba(14, 231, 204, 0.01)
    );
  }
}
</style>
